import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTransactions } from '../context/TransactionContext';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

function AIAssistant() {
  const { transactions, getStats } = useTransactions();
  const { formatCurrency } = useSettings();
  const { token } = useAuth();
  // const [activeTab, setActiveTab] = useState('overview');
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: 'Hello! I am your AI Financial Advisor. How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [aiInsights, setAiInsights] = useState([]);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const messagesEndRef = useRef(null);

  const stats = getStats();
  const savingsRate = stats.monthlyIncome > 0 
    ? ((stats.monthlyIncome - stats.monthlyExpenses) / stats.monthlyIncome) * 100 
    : 0;

  const handleSendMessage = async (customText) => {
    const textToSend = customText || inputMessage;
    if (!textToSend.trim() || isTyping) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    if (!customText) setInputMessage('');
    setIsTyping(true);

    try {
      const res = await api.aiChat(textToSend, stats, transactions, token);

      const aiResponse = {
        id: messages.length + 2,
        type: 'ai',
        content: res.message || "I'm having trouble processing your request.",
        timestamp: new Date(),
        fallback: res.fallback || false
      };

      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('AI Chat Error:', error);
      
      const fallbackResponse = {
        id: messages.length + 2,
        type: 'ai',
        content: `Based on your current profile — Income: ${formatCurrency(stats.monthlyIncome)}, Expenses: ${formatCurrency(stats.monthlyExpenses)}, Savings Rate: ${savingsRate.toFixed(1)}% — I recommend focusing on expense tracking and maintaining your current savings trajectory.`,
        timestamp: new Date(),
        fallback: true
      };

      setMessages(prev => [...prev, fallbackResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  // Function to generate AI insights using real transaction data via backend API
  const generateAIInsights = useCallback(async () => {
    setIsLoadingInsights(true);
    try {
      const res = await api.aiInsights(stats, transactions, token);

      if (res.insights) {
        const insightArray = typeof res.insights === 'string'
          ? res.insights.split('\n')
              .filter(line => line.trim() && /^\d+\./.test(line.trim()))
              .map(line => line.replace(/^\d+\.\s*/, '').trim())
          : res.insights;

        setAiInsights(insightArray.length > 0 ? insightArray : [res.insights]);
        setLastUpdateTime(new Date());
      }
    } catch (error) {
      console.error('AI Insights Error:', error);
      setAiInsights([
        "Focus on tracking your highest expense categories to identify optimization opportunities.",
        "Maintain your current savings rate while looking for additional income streams.",
        "Consider automating savings to ensure consistent financial progress."
      ]);
      setLastUpdateTime(new Date());
    } finally {
      setIsLoadingInsights(false);
    }
  }, [transactions, stats, token]);

  // Auto-generate insights every 24 hours
  useEffect(() => {
    if (transactions.length > 0 && token) {
      const now = new Date();
      const shouldUpdate = !lastUpdateTime || 
        (now.getTime() - lastUpdateTime.getTime()) >= (24 * 60 * 60 * 1000); // 24 hours in milliseconds
      
      if (shouldUpdate) {
        generateAIInsights();
      }
    }
  }, [transactions.length, token, lastUpdateTime, generateAIInsights]);

  // Set up 24-hour timer for automatic updates
  useEffect(() => {
    if (lastUpdateTime) {
      const nextUpdate = new Date(lastUpdateTime.getTime() + (24 * 60 * 60 * 1000));
      const timeUntilUpdate = nextUpdate.getTime() - new Date().getTime();
      
      if (timeUntilUpdate > 0) {
        const timer = setTimeout(() => {
          generateAIInsights();
        }, timeUntilUpdate);
        
        return () => clearTimeout(timer);
      }
    }
  }, [lastUpdateTime, generateAIInsights]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Helper function to send quick questions
  const sendQuickQuestion = (question) => {
    if (!isTyping) {
      handleSendMessage(question);
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            AI Financial Assistant
          </h1>
          <p style={{ color: 'var(--gray-600)' }}>
            AI-powered financial insights and personalized recommendations
          </p>
        </div>

        {/* Top Stats Row */}
        <div className="stats-grid" style={{ marginBottom: '2rem' }}>
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-title">Total Transactions</span>
              <span className="stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                </svg>
              </span>
            </div>
            <div className="stat-value">{transactions.length.toLocaleString()}</div>
            <div className="stat-change positive" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
              All time data analyzed
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-title">Monthly Income</span>
              <span className="stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="m16 6 2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                </svg>
              </span>
            </div>
            <div className="stat-value">{formatCurrency(stats.monthlyIncome)}</div>
            <div className="stat-change positive" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
              Average earnings
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-title">Monthly Expenses</span>
              <span className="stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="m16 18 2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z"/>
                </svg>
              </span>
            </div>
            <div className="stat-value">{formatCurrency(stats.monthlyExpenses)}</div>
            <div className="stat-change" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
              Average spending
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-title">Savings Rate</span>
              <span className="stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15 10.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5 1.5.67 1.5 1.5zm4-6.5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H5V8h14v10z"/>
                </svg>
              </span>
            </div>
            <div className="stat-value">{savingsRate.toFixed(1)}%</div>
            <div className={`stat-change ${savingsRate >= 10 ? 'positive' : ''}`} style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
              {savingsRate >= 20 ? 'Excellent performance' : savingsRate >= 10 ? 'Good progress' : 'Needs improvement'}
            </div>
          </div>
        </div>

        {/* Main Layout: Chat + Sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
          
          {/* Left Side: Large AI Chat Card */}
          <div className="card">
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                AI Financial Advisor
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>
                Ask questions about budgeting, spending analysis, and savings strategies
              </p>
            </div>

            {/* Chat Messages */}
            <div style={{
              background: 'var(--gray-50)',
              minHeight: '400px',
              maxHeight: '500px',
              overflowY: 'auto',
              padding: '1rem',
              borderRadius: '0.5rem',
              border: '1px solid var(--gray-200)',
              marginBottom: '1rem'
            }}>
              {messages.map((msg, index) => (
                <div key={index} style={{ 
                  marginBottom: '1rem',
                  display: 'flex',
                  justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                }}>
                  <div style={{
                    maxWidth: '80%',
                    padding: '0.75rem 1rem',
                    borderRadius: msg.type === 'user' ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                    background: msg.type === 'user' ? 'var(--gray-900)' : 'var(--white)',
                    color: msg.type === 'user' ? 'var(--white)' : 'var(--gray-900)',
                    border: msg.type === 'ai' ? '1px solid var(--gray-200)' : 'none',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-line',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div style={{ 
                  marginBottom: '1rem',
                  display: 'flex',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '1rem 1rem 1rem 0.25rem',
                    background: 'var(--white)',
                    border: '1px solid var(--gray-200)',
                    fontSize: '0.75rem',
                    color: 'var(--gray-500)',
                    fontStyle: 'italic',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    AI is thinking...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Ask me about your finances..."
                rows={2}
                style={{
                  flex: 1,
                  resize: 'none',
                  fontSize: '0.875rem',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '12px',
                  padding: '0.75rem 1rem',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  transition: 'border-color 0.2s ease'
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isTyping}
                className="btn btn-primary"
                style={{
                  alignSelf: 'flex-end',
                  minWidth: '80px',
                  height: 'fit-content'
                }}
              >
                {isTyping ? 'Sending...' : 'Send'}
              </button>
            </div>

          </div>

          {/* Right Side: Two Small Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* AI Insights Card */}
            <div className="card">
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>
                Real-Time AI Insights
                {isLoadingInsights && (
                  <div style={{ 
                    width: '14px', 
                    height: '14px', 
                    border: '2px solid var(--gray-300)', 
                    borderTop: '2px solid var(--primary-color)', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite',
                    display: 'inline-block',
                    marginLeft: '0.5rem'
                  }}></div>
                )}
              </h3>
              
              {aiInsights.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {aiInsights.slice(0, 3).map((insight, index) => (
                    <div key={index} style={{ 
                      padding: '0.75rem', 
                      background: 'var(--gray-50)', 
                      borderRadius: '0.5rem',
                      borderLeft: '3px solid var(--income-green)',
                      fontSize: '0.875rem',
                      color: 'var(--gray-700)'
                    }}>
                      {insight}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', fontStyle: 'italic' }}>
                  Loading insights from your data...
                </p>
              )}

            </div>

            {/* Quick Analysis Card */}
            <div className="card">
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>
                Quick Analysis
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => sendQuickQuestion("Analyze my top spending categories")}
                  disabled={isTyping}
                  style={{ 
                    padding: '0.75rem', 
                    textAlign: 'left', 
                    fontSize: '0.875rem',
                    width: '100%'
                  }}
                >
                  Analyze spending categories
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => sendQuickQuestion("What's my current financial health?")}
                  disabled={isTyping}
                  style={{ 
                    padding: '0.75rem', 
                    textAlign: 'left', 
                    fontSize: '0.875rem',
                    width: '100%'
                  }}
                >
                  Check financial health
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => sendQuickQuestion("Give me investment advice")}
                  disabled={isTyping}
                  style={{ 
                    padding: '0.75rem', 
                    textAlign: 'left', 
                    fontSize: '0.875rem',
                    width: '100%'
                  }}
                >
                  Investment suggestions
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => sendQuickQuestion("How to reduce monthly expenses?")}
                  disabled={isTyping}
                  style={{ 
                    padding: '0.75rem', 
                    textAlign: 'left', 
                    fontSize: '0.875rem',
                    width: '100%'
                  }}
                >
                  Expense reduction tips
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default AIAssistant;
